import { memo, useState, useEffect } from "react";
import Accordion from "../../../components/accordian";
import { useTranslation } from "react-i18next";
import {
  Controller,
  UseFormClearErrors,
  UseFormSetValue,
} from "react-hook-form";
import DateTimePickerInput from "../../../components/inputs/DateTimePickerInput";
import toast from "react-hot-toast";
import { WAREHOUSE_IN_OUT_PERMIT_VALIDITY_NOTES } from "../../../constants/common";
import { formattedDateTime } from "../../../lib/functions";

interface PermitValidityProps {
  clearErrors: UseFormClearErrors<{
    exitFromBuilding?: string | undefined;
    entryIntoBuilding?: string | undefined;
    escortEmployee: string;
    uploadEscortingEmployeeIDCard?: any;
    permitStartDate: string;
    permitEndDate: string;
  }>;
  setValue: UseFormSetValue<{
    exitFromBuilding?: string | undefined;
    entryIntoBuilding?: string | undefined;
    generalDescriptionOther?: string | undefined;
    escortEmployee: string;
    uploadEscortingEmployeeIDCard?: any;
    permitStartDate: string;
    permitEndDate: any;
  }>;
  control: any;
  errors: any;
  permitStartDate: Date;
  quantityOfItems: number;
  isFieldsDisabled: boolean;
}

const PermitValidity = ({
  clearErrors,
  control,
  errors,
  setValue,
  permitStartDate,
  quantityOfItems,
  isFieldsDisabled,
}: PermitValidityProps) => {
  const [selectedKey, setSelectedKey] = useState<string>("1");

  const { t } = useTranslation();

  const [maxEndDate, setMaxEndDate] = useState<Date>();
  const [selectedStartDate, setSelectedStartDate] =
    useState<Date>(permitStartDate);

  // Calculate max permit validity based on quantity
  const getMaxValidityDays = (quantity: number) => {
    return quantity < 50 ? 1 : 3;
  };
  // Initialize maxEndDate based on default permitStartDate and quantityOfItems
  useEffect(() => {
    if (permitStartDate) {
      const maxDate = new Date(permitStartDate);
      const maxValidityDays = getMaxValidityDays(quantityOfItems);
      
      if (maxValidityDays === 1) {
        // For 1 day: same day at 23:59:59
        // e.g., 15/1/26 9:07am -> 15/1/26 23:59:59
        maxDate.setHours(23, 59, 59, 999);
      } else {
        // For 3 days: start day + 2 more days at 23:59:59
        // e.g., 15/1/26 9:07am -> 17/1/26 23:59:59
        maxDate.setDate(maxDate.getDate() + 2);
        maxDate.setHours(23, 59, 59, 999);
      }
      
      setMaxEndDate(maxDate);
    }
  }, [permitStartDate, quantityOfItems]);
  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordion
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 items-end -mt-2">
            {t("5")}. {t("permitValidity")}
          </h1>
        }
      >
        <div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
            <Controller
              name="permitStartDate"
              control={control}
              render={({ field: { name, value, onChange } }) => (
                <DateTimePickerInput
                  label={t("permitStartDate")}
                  value={value}
                  required
                  readOnly={isFieldsDisabled}
                  onChange={(value) => {
                    if (!value) return;

                    onChange(value);
                    clearErrors(name);

                    // Set the selected start date for end date min validation
                    const startDate = new Date(value);
                    setSelectedStartDate(startDate);

                    // Set max end date based on quantity (day-based, not time-based)
                    const maxDate = new Date(value);
                    const maxValidityDays = getMaxValidityDays(quantityOfItems);
                    
                    if (maxValidityDays === 1) {
                      // For 1 day: same day at 23:59:59
                      // e.g., 15/1/26 9:07am -> 15/1/26 23:59:59
                      maxDate.setHours(23, 59, 59, 999);
                    } else {
                      // For 3 days: start day + 2 more days at 23:59:59
                      // e.g., 15/1/26 9:07am -> 17/1/26 23:59:59
                      maxDate.setDate(maxDate.getDate() + 2);
                      maxDate.setHours(23, 59, 59, 999);
                    }
               
                    setMaxEndDate(maxDate);

                    // Auto-set end date to start date + 1 hour
                    const autoEndDate = new Date(startDate);
                    autoEndDate.setHours(autoEndDate.getHours() + 1);

                    // Make sure the auto-set end date doesn't exceed the max allowed date
                    const finalEndDate = autoEndDate > maxDate ? maxDate : autoEndDate;
                    
                    setValue("permitEndDate", formattedDateTime(finalEndDate));
                  }}
                  error={errors?.[name]?.message}
                  minDate={new Date()}
                  dateTimeValidation={(newDate: Date) => {
                    if (
                      // location.pathname !== APP_ROUTES.APPROVE_REQUEST &&
                      // location.pathname !== APP_ROUTES.UPDATE_REQUEST &&
                      newDate < new Date()
                    ) {
                      toast.error(t("permitStartTimeCannotBeInPast"));
                      return false;
                    }
                    return true;
                  }}
                />
              )}
            />

            <Controller
              name="permitEndDate"
              control={control}
              render={({ field: { name, value, onChange } }) => {
                const currentMaxDate = maxEndDate;

                return (
                  <DateTimePickerInput
                    label={t("permitEndDate")}
                    value={value}
                    required
                    readOnly={isFieldsDisabled}
                    onChange={(value) => {
                      // Ensure the selected value doesn't exceed maxDate
                      if (
                        currentMaxDate &&
                        value &&
                        new Date(value) > currentMaxDate
                      ) {
                        onChange(currentMaxDate);
                      } else {
                        onChange(value);
                      }
                      clearErrors(name);
                    }}
                    error={errors?.[name]?.message}
                    minDate={selectedStartDate}
                    maxDate={currentMaxDate}
                    dateTimeValidation={(newDate: Date) => {
                      if (newDate < selectedStartDate) {
                        toast.error(t("permitEndTimeGreaterThanStartTime"));
                        return false;
                      }
                      
                      if (currentMaxDate && newDate > currentMaxDate) {
                        toast.error(
                          quantityOfItems < 50
                            ? t("permitValidityCannotExceedOneDay")
                            : t("permitValidityCannotExceedThreeDays")
                        );
                        return false;
                      }
                      
                      // Compare only dates (ignore time)
                      if (currentMaxDate) {
                        const newDateOnly = new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());
                        const maxDateOnly = new Date(currentMaxDate.getFullYear(), currentMaxDate.getMonth(), currentMaxDate.getDate());
                        
                        if (newDateOnly > maxDateOnly) {
                          toast.error(
                            quantityOfItems < 50
                              ? t("permitValidityCannotExceedOneDay")
                              : t("permitValidityCannotExceedThreeDays")
                          );
                          return false;
                        }
                      }
                      
                      return true;
                    }}
                  />
                );
              }}
            />
          </div>

          <p className="text-red-600 text-xs mt-4">
            <span className="font-semibold">{t("note")}:</span>{" "}
            <ul className="list-disc list-inside">
              {WAREHOUSE_IN_OUT_PERMIT_VALIDITY_NOTES.map((key) => (
                <li key={key}>{t(key)}</li>
              ))}
            </ul>
          </p>
        </div>
      </Accordion>
    </div>
  );
};

export default memo(PermitValidity)
